import { SnackbarProvider } from 'notistack'
import { createRoot } from 'react-dom/client'
import { IntlProvider } from 'react-intl'
import { Provider } from 'react-redux'
import { BrowserRouter as Router } from 'react-router-dom'
import 'typeface-roboto'
import AppShell from './AppShell'
import { AUTH_DISABLED } from './app/axios/const'
import { SnackbarUtilsConfigurator } from './app/snackbar/snackbarUtil'
import { store } from './app/store/store'
import IdentityConfigLoader from './features/Auth/IdentityConfigLoader'
import IdleSessionPreflight from './features/Auth/IdleSessionPreflight'
import RequestInterceptor from './components/RequestIntercepter/RequestIntercepter'
import DevApp from './DevApp'
import SignupApp from './features/Signup/SignupApp'
import './index.css'
import reportWebVitals from './reportWebVitals'

// Suppress benign ResizeObserver loop errors (caused by charting libraries like AnyChart)
window.addEventListener('error', (e: ErrorEvent) => {
  if (
    e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
    e.message === 'ResizeObserver loop limit exceeded'
  ) {
    e.stopImmediatePropagation()
  }
})

const container = document.getElementById('root') as HTMLElement
const root = createRoot(container)

// E4 F4-1: the public /signup flow renders OUTSIDE MsalProvider + RequestInterceptor so an
// unauthenticated visitor is never bounced into the Entra sign-in redirect (App.tsx's
// useMsalAuthentication). A full-page boundary between /signup and the authed app is fine -- they're
// separate auth contexts, and the verification link lands on a tenant subdomain (a different host).
const isSignupRoute = window.location.pathname.startsWith('/signup')

if (isSignupRoute) {
  root.render(
    <Provider store={store}>
      <IntlProvider locale='en'>
        <Router>
          <SnackbarProvider maxSnack={3}>
            <SnackbarUtilsConfigurator />
            <SignupApp />
          </SnackbarProvider>
        </Router>
      </IntlProvider>
    </Provider>
  )
} else if (AUTH_DISABLED) {
  // Docker development mode: render without MSAL authentication
  console.log('Auth disabled: running in Docker development mode without Azure AD')
  root.render(
    <Provider store={store}>
      <IntlProvider locale='en'>
        <Router>
          <SnackbarProvider maxSnack={3}>
            <SnackbarUtilsConfigurator />
            <DevApp />
          </SnackbarProvider>
        </Router>
      </IntlProvider>
    </Provider>
  )
} else {
  // Production mode (E7 F7-5.1): IdentityConfigLoader fetches /identity-config, builds MSAL from the
  // per-Tenant result, and wraps the app in MsalProvider — or renders InvalidWorkspacePage on a 404.
  // MSAL construction + initialize() + the init-failure fallback now live inside the loader.
  root.render(
    <IdentityConfigLoader>
      <Provider store={store}>
        <IntlProvider locale='en'>
          <RequestInterceptor>
            <IdleSessionPreflight />
            <Router>
              <SnackbarProvider maxSnack={3}>
                <SnackbarUtilsConfigurator />
                <AppShell />
              </SnackbarProvider>
            </Router>
          </RequestInterceptor>
        </IntlProvider>
      </Provider>
    </IdentityConfigLoader>
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
